import { useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Search, Pencil, Trash2, FlaskConical, GitBranch, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DataTable } from '@/components/shared/DataTable';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { JsonViewer } from '@/components/shared/JsonViewer';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useGetPoliciesQuery,
  useCreatePolicyMutation,
  useUpdatePolicyMutation,
  useDeletePolicyMutation,
  useReactivatePolicyMutation,
  useEvaluatePolicyMutation,
} from '@/store/api/apiSlice';
import { useToast } from '@/components/shared/Toast';
import { PolicyBuilder, type PolicyBuilderValue } from '@/components/policies/PolicyBuilder';
import type { Policy } from '@/types';
import { format } from 'date-fns';

export function PoliciesPage() {
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Policy | null>(null);
  const [evalModalOpen, setEvalModalOpen] = useState(false);
  const [evalTarget, setEvalTarget] = useState<Policy | null>(null);

  // Server-side search: the term is sent to `GET /policies?search=` (matches
  // name OR description). Listing starts at page 1; the visible DataTable owns
  // client-side pagination of the returned result set.
  const { data, isLoading } = useGetPoliciesQuery({
    page: 1,
    pageSize: 50,
    search: search || undefined,
  });
  const toast = useToast();
  const [createPolicy, { isLoading: isCreating }] = useCreatePolicyMutation();
  const [updatePolicy, { isLoading: isUpdating }] = useUpdatePolicyMutation();
  const [deletePolicy, { isLoading: isDeleting }] = useDeletePolicyMutation();
  const [reactivatePolicy] = useReactivatePolicyMutation();
  const [evaluatePolicy, { data: evalResult, isLoading: isEvaluating }] = useEvaluatePolicyMutation();

  const policies = (data as { data?: Policy[] } | undefined)?.data ?? [];

  // Evaluation form state
  const [evalAction, setEvalAction] = useState('');
  const [evalResource, setEvalResource] = useState('');

  // Editor form state — driven by the shared PolicyBuilder (PB-1).
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [builder, setBuilder] = useState<PolicyBuilderValue>({
    name: '',
    description: '',
    statements: [{ effect: 'Allow', actions: [], resources: [], conditions: [] }],
    priority: 0,
    rateLimit: { requestsPerMinute: undefined, requestsPerHour: undefined },
  });

  const columns: ColumnDef<Policy, unknown>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div>
          <p className="font-medium">{row.original.name}</p>
          {row.original.description && (
            <p className="text-xs text-muted-foreground truncate max-w-xs">{row.original.description}</p>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => (
        <StatusBadge status={row.original.isActive ? 'active' : 'stopped'} />
      ),
    },
    {
      accessorKey: 'statements',
      header: 'Statements',
      cell: ({ row }) => `${row.original.statements.length} rule(s)`,
    },
    {
      accessorKey: 'version',
      header: 'Version',
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
    },
    {
      accessorKey: 'updatedAt',
      header: 'Updated',
      cell: ({ row }) => format(new Date(row.original.updatedAt), 'MMM d, yyyy'),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex gap-1">
          {row.original.cascadeDisabledAt && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleReactivate(row.original)}
              title="Reactivate"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={() => openEval(row.original)} title="Evaluate">
            <FlaskConical className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => openEdit(row.original)} title="Edit">
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(row.original)} title="Delete">
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ];

  function openCreate() {
    setEditingPolicy(null);
    setFormName('');
    setFormDescription('');
    setBuilder({
      name: '',
      description: '',
      statements: [{ effect: 'Allow', actions: [], resources: [], conditions: [] }],
      priority: 0,
      rateLimit: { requestsPerMinute: undefined, requestsPerHour: undefined },
    });
    setModalOpen(true);
  }

  function openEdit(policy: Policy) {
    setEditingPolicy(policy);
    setFormName(policy.name);
    setFormDescription(policy.description ?? '');
    setBuilder({
      name: policy.name,
      description: policy.description ?? '',
      statements: (policy.statements ?? []).map((s) => ({
        effect: s.effect,
        actions: [...s.actions],
        resources: [...s.resources],
        conditions: s.conditions ?? [],
      })),
      priority: policy.priority ?? 0,
      parentPolicyId: policy.parentPolicyId ?? undefined,
      rateLimit: (policy as Policy & { rateLimit?: { requestsPerMinute?: number; requestsPerHour?: number } }).rateLimit,
    });
    setModalOpen(true);
  }

  function openEval(policy: Policy) {
    setEvalTarget(policy);
    setEvalAction('');
    setEvalResource('');
    setEvalModalOpen(true);
  }

  async function handleSave() {
    const rateLimit =
      builder.rateLimit && (builder.rateLimit.requestsPerMinute || builder.rateLimit.requestsPerHour)
        ? { requestsPerMinute: builder.rateLimit.requestsPerMinute, requestsPerHour: builder.rateLimit.requestsPerHour }
        : undefined;

    // `ttlSeconds` is an EPHEMERAL-only concept (POST /policies/ephemeral) and
    // is rejected by the standard create/update schemas, so the UI must never
    // send it here. The builder's TTL is instead translated into the accepted
    // `expiresAt`, anchored at `effectiveAt` (or now when unset).
    const validity = builder.ttlSeconds
      ? {
          effectiveAt: builder.effectiveAt ?? new Date().toISOString(),
          expiresAt: new Date(
            (builder.effectiveAt ? new Date(builder.effectiveAt).getTime() : Date.now()) +
              builder.ttlSeconds * 1000,
          ).toISOString(),
        }
      : builder.effectiveAt
        ? { effectiveAt: builder.effectiveAt }
        : {};

    const body = {
      name: formName,
      description: formDescription,
      statements: builder.statements,
      ...(builder.priority !== undefined ? { priority: builder.priority } : {}),
      ...(builder.parentPolicyId ? { parentPolicyId: builder.parentPolicyId } : {}),
      ...(rateLimit ? { rateLimit } : {}),
      ...validity,
    };
    if (editingPolicy) {
      await updatePolicy({ id: editingPolicy._id, ...body }).unwrap();
    } else {
      await createPolicy(body).unwrap();
    }
    setModalOpen(false);
  }

  async function handleDelete() {
    if (deleteTarget) {
      await deletePolicy(deleteTarget._id).unwrap();
      setDeleteTarget(null);
    }
  }

  /**
   * Undo a cascade-disable (or soft-delete) on one policy. Only shown for
   * rows carrying `cascadeDisabledAt` — a policy that was never disabled has
   * nothing to recover (the backend replies 409 otherwise).
   */
  async function handleReactivate(policy: Policy) {
    try {
      await reactivatePolicy(policy._id).unwrap();
      toast.success(`Policy "${policy.name}" reactivated`);
    } catch (err) {
      toast.error(
        (err as { data?: { message?: string } })?.data?.message ??
          'Failed to reactivate policy',
      );
    }
  }

  async function handleEvaluate() {
    if (evalTarget) {
      await evaluatePolicy({
        policyId: evalTarget._id,
        action: evalAction,
        resource: evalResource,
      });
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Policies</h1>
          <p className="text-sm text-muted-foreground">IAM-style policy management with hierarchy</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Create Policy
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search policies..."
              aria-label="Search policies"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : policies.length === 0 ? (
            <EmptyState
              title="No policies found"
              description="Create your first policy to control access"
              action={<Button onClick={openCreate}>Create Policy</Button>}
            />
          ) : (
            <DataTable columns={columns} data={policies} />
          )}
        </CardContent>
      </Card>

      {/* Policy Editor Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPolicy ? 'Edit Policy' : 'Create Policy'}</DialogTitle>
            <DialogDescription>
              Define policy statements with effects, actions, and resources.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={formName} onChange={(e) => setFormName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input value={formDescription} onChange={(e) => setFormDescription(e.target.value)} />
              </div>
            </div>

            {/* Shared Policy Builder (PB-1): statements + rate limits + validity */}
            <PolicyBuilder
              value={{ ...builder, name: formName, description: formDescription }}
              onChange={(next) => {
                setBuilder(next);
                if (next.name !== undefined) setFormName(next.name);
                if (next.description !== undefined) setFormDescription(next.description);
              }}
              showTemplates={false}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isCreating || isUpdating}>
              {(isCreating || isUpdating) ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Evaluate Policy Modal */}
      <Dialog open={evalModalOpen} onOpenChange={setEvalModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Evaluate Policy</DialogTitle>
            <DialogDescription>
              Test policy "{evalTarget?.name}" with an action and resource.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Action</Label>
              <Input
                placeholder="e.g., users:read"
                value={evalAction}
                onChange={(e) => setEvalAction(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Resource</Label>
              <Input
                placeholder="e.g., arn:cluster:users/*"
                value={evalResource}
                onChange={(e) => setEvalResource(e.target.value)}
              />
            </div>
            {evalResult && (
              <div className="space-y-2">
                <Label>Result</Label>
                <JsonViewer data={evalResult} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEvalModalOpen(false)}>
              Close
            </Button>
            <Button onClick={handleEvaluate} disabled={isEvaluating || !evalAction || !evalResource}>
              {isEvaluating ? 'Evaluating...' : 'Evaluate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Policy"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />
    </div>
  );
}
